import React, { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockBarAnchor } from '../../../../_lib/components/BlockBar';
import { Icons } from '../../../atoms/SVG';
import Btn, { BtnModifier } from '../../../molecules/Btn';
import { TooltipPlacement } from '../../../atoms/Tooltip';
import { ButtonConfig, TeamroomView } from '../TeamroomManagement.types';
import { Namespace } from '../../../../translation/namespace';
import { LazyView } from '../../../../types/BlockWithViews';
import { BUTTON_CONFIGS } from '../TeamroomManagement.constants';

type TeamroomManagementButtonsProps = {
  views: Record<TeamroomView, LazyView> | null | undefined;
  currentActiveView: TeamroomView | null;
  toggleView: (viewKey: TeamroomView) => void;
  blockId: string;
};

export function TeamroomManagementButtons({
  views,
  currentActiveView,
  toggleView,
  blockId,
}: TeamroomManagementButtonsProps) {
  const { t } = useTranslation(Namespace.TeamroomManagement);

  if (!views) {
    return null;
  }

  const renderButton = (config: ButtonConfig) => {
    const view = views[config.key];

    if (!view) {
      return null;
    }

    const text = t(`${Namespace.TeamroomManagement}:actions.${config.key}.text`);
    const isActive = currentActiveView === config.key;
    const embedded = (view as { embedded?: unknown }).embedded;
    const hasForm = embedded && (embedded as { form?: unknown }).form;
    const isDisabled = !hasForm;

    switch (config.variant) {
      case 'anchor': {
        const href = (view as { href?: string }).href;

        if (!href) {
          return null;
        }

        return (
          <BlockBarAnchor
            key={config.key}
            href={`${href}#${blockId}`}
            onClick={(e: MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              toggleView(config.key);
            }}
            text={text}
            svgSpriteId={isActive ? Icons.DECLINE_CROSS : config.icon}
            {...(!config.isModal && {
              'aria-controls': `block-content-${blockId}`,
              'aria-expanded': isActive,
            })}
          />
        );
      }
      case 'iconButton':
        return (
          <Btn
            key={config.key}
            modifier={[
              BtnModifier.SINGLE_ACTION,
              ...(Array.isArray(config.modifier) ? config.modifier : config.modifier ? [config.modifier] : []),
            ]}
            text={text}
            onClick={() => toggleView(config.key)}
            tooltipPlacement={TooltipPlacement.TOP}
            svgSpriteId={isActive ? Icons.DECLINE_CROSS : config.icon!}
            hiddenText
          />
        );
      case 'textButton':
        return (
          <Btn
            key={config.key}
            modifier={[BtnModifier.SINGLE_ACTION]}
            titleText={text}
            text={text}
            onClick={() => toggleView(config.key)}
            tooltipPlacement={TooltipPlacement.TOP}
            ariaDisabled={isDisabled}
          />
        );
      default:
        return null;
    }
  };

  return <div className="teamroom-management__buttons-container">{BUTTON_CONFIGS.map(renderButton)}</div>;
}
